// ============================================================================
// Storage Utilities (IndexedDB + localStorage)
// ============================================================================

const DB_NAME = 'qr-menu-db'
const DB_VERSION = 1
const STORE_NAME = 'menu-data'

// ============================================================================
// IndexedDB Wrapper
// ============================================================================

export class IndexedDB {
  private db: IDBDatabase | null = null

  async open(): Promise<IDBDatabase> {
    if (this.db) {
      return this.db
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => {
        reject(new Error('Failed to open IndexedDB'))
      }

      request.onsuccess = () => {
        this.db = request.result
        resolve(this.db)
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME)
        }
      }
    })
  }

  async get<T>(key: string): Promise<T | null> {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.get(key)

      request.onsuccess = () => {
        resolve(request.result || null)
      }

      request.onerror = () => {
        reject(new Error(`Failed to get key: ${key}`))
      }
    })
  }

  async set<T>(key: string, value: T): Promise<void> {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.put(value, key)

      request.onsuccess = () => {
        resolve()
      }

      request.onerror = () => {
        reject(new Error(`Failed to set key: ${key}`))
      }
    })
  }

  async delete(key: string): Promise<void> {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.delete(key)

      request.onsuccess = () => {
        resolve()
      }

      request.onerror = () => {
        reject(new Error(`Failed to delete key: ${key}`))
      }
    })
  }

  async clear(): Promise<void> {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.clear()

      request.onsuccess = () => {
        resolve()
      }

      request.onerror = () => {
        reject(new Error('Failed to clear store'))
      }
    })
  }

  async getAllKeys(): Promise<string[]> {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.getAllKeys()

      request.onsuccess = () => {
        resolve(request.result as string[])
      }

      request.onerror = () => {
        reject(new Error('Failed to get all keys'))
      }
    })
  }

  close(): void {
    if (this.db) {
      this.db.close()
      this.db = null
    }
  }
}

// Singleton instance
const idb = new IndexedDB()

// ============================================================================
// localStorage Helpers (with error handling)
// ============================================================================

export function localStorageGet<T>(key: string, defaultValue: T): T {
  try {
    const item = window.localStorage.getItem(key)
    if (item === null) {
      return defaultValue
    }
    return JSON.parse(item) as T
  } catch (error) {
    console.error(`Failed to get localStorage key: ${key}`, error)
    return defaultValue
  }
}

export function localStorageSet<T>(key: string, value: T): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.error(`Failed to set localStorage key: ${key}`, error)
    // If quota exceeded, try to clear old data
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.warn('localStorage quota exceeded, clearing old data')
      clearOldLocalStorageData()
      try {
        window.localStorage.setItem(key, JSON.stringify(value))
      } catch (retryError) {
        console.error('Failed to set localStorage after clearing old data', retryError)
      }
    }
  }
}

export function localStorageDelete(key: string): void {
  try {
    window.localStorage.removeItem(key)
  } catch (error) {
    console.error(`Failed to delete localStorage key: ${key}`, error)
  }
}

export function localStorageClear(): void {
  try {
    window.localStorage.clear()
  } catch (error) {
    console.error('Failed to clear localStorage', error)
  }
}

/**
 * Clear old localStorage data (keep only recent cart, language, theme)
 */
function clearOldLocalStorageData(): void {
  const keysToKeep = ['cart', 'language', 'theme', 'favorites', 'last-ordered']
  const allKeys = Object.keys(window.localStorage)

  allKeys.forEach((key) => {
    if (!keysToKeep.includes(key)) {
      try {
        window.localStorage.removeItem(key)
      } catch (e) {
        // Ignore errors during cleanup
      }
    }
  })
}

// ============================================================================
// Export IndexedDB instance
// ============================================================================

export { idb }
